import { track } from '@sloyd/analytics';
import { actions, selectors } from '@sloyd/store-base';
import { toast } from '@sloyd/ui';
import { delay, fork, put, select, takeEvery } from 'saga-ts';
import { beep, guid4, invokeEvent, l } from 'shared-base';
import { setTileId } from '../utils/globals';
import * as rt from '../utils/realtime';
import { customEventChannel } from './channels/channel.customEvent';
import { predicateAppState } from './helpers/predicates';

export const saga = {
  id: 'sloyd.realtime',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['realtime'],
  },
};

type Verb =
  | 'ready' //
  | 'start'
  | 'stop'
  | 'tileHover'
  | 'transcript'
  | 'describeViewport'
  | 'functionStart';

const map: Record<Verb, any> = {
  ready: ready,
  start: start,
  stop: stop,
  functionStart: functionStart,
  transcript: transcript,
  tileHover: tileHover,
  describeViewport: describeViewport,
};

const analyitcsMaprealtimes: any = {
  start: 'Start Realtime',
  stop: 'Stop Realtime',
};

const localTypes: EditorCommandType[] = [
  'randomize',
  'download',
  'download-image',
  'environment',
  'add-model',
  'add-character',
  'start-scene',
  'scene-instructions',
  'style-transfer',
  'switch-mode',
  'clear-scene',
];

export function* ready(ev: any) {
  try {
    yield delay(350);
    yield put(actions.realtimeState.patch({ isReady: true }));
  } catch (err) {}
}

export function* describeViewport(ev: any) {
  try {
    yield delay(20);

    const appState = yield* select(selectors.raw.$rawAppState);
    const { editMode } = appState;

    let message = '';

    if (editMode !== 'scene') {
      const currentModel = yield* select(selectors.single.$currentModel);
      message = 'There is currently a model in the scene';

      if (currentModel) {
        message += `: ${currentModel.name}`;
      }
    } else {
      message = 'There is now an empty scene';
    }

    rt.send(message, true);
  } catch (err) {}
}

export function* tileHover(ev: any) {
  try {
    const { data } = ev;
    const { tileId } = data;
    setTileId(tileId);
  } catch (err) {
    console.log('err =>', err);
  }
}

export function* transcript(ev: any) {
  try {
    const { data } = ev;
    const { content } = data;

    yield put(
      actions.realtimeState.patch({
        subtitles: content,
        subtitlesTs: Date.now(),
      })
    );
  } catch (err) {
    console.log('err =>', err);
  }
}

export function* functionStart(ev: any) {
  try {
    const { data } = ev;
    const { name, call_id: realtimeId, params } = data;

    beep();

    if (name === 'end-session') {
      yield put(actions.realtimeState.patch({ isEnabled: false }));
      rt.stop(true);
      return;
    }

    const id = guid4();

    const isLocal = localTypes.includes(name);

    const realtimeContext = yield* select(selectors.realtime.$context);

    const callItem: IEditorCommand = {
      id,
      ts: Date.now(),
      status: 'pending',
      type: name,
      params,
      isLocal,
      realtimeId,
      context: realtimeContext,
    };

    yield put(actions.editorCommands.patch(id, callItem));

    yield rt.functionResponse(realtimeId, {
      message: 'request added to queue',
    });
  } catch (err) {}
}

export function* start(ev: any) {
  try {
    const settings = yield* select(selectors.raw.$rawSettings);

    const { openAiKey } = settings;

    if (!openAiKey) {
      toast.error('Realtime requires an OpenAI key. Define it in the settings.');
      return;
    }

    yield put(actions.realtimeState.patch({ isEnabled: true, isReady: false }));
    yield put(actions.editorCommands.setAll({}));
    rt.init(openAiKey);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* stop(ev: any) {
  try {
    yield put(actions.realtimeState.patch({ isEnabled: false }));
    rt.stop();
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* realtime(ev: any) {
  const { data } = ev;
  const { verb } = data;

  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.realtime ${verb}`);

  track(analyitcsMaprealtimes[verb], { origin });

  yield* fork(saga, ev);
}

export function* root() {
  const channel = customEventChannel('REALTIME');
  yield takeEvery(channel, realtime);

  yield takeEvery(predicateAppState('editMode'), describeViewport);
}
